<template>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 p-3.5 bg-card border border-border rounded-xl shadow-sm" aria-label="Bilan annuel">
    <div class="md:col-span-2 flex items-center gap-3 mb-1">
      <div class="w-1 h-4 bg-primary rounded-full" aria-hidden="true"></div>
      <h2 class="text-xs font-black uppercase tracking-widest text-foreground">Bilan Annuel {{ year }}</h2>
    </div>

    <!-- Personal account card -->
    <div class="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <span class="text-lg leading-none">🏠</span>
        <span class="text-xs font-black uppercase tracking-wider text-emerald-400">Compte Perso</span>
      </div>
      <div class="space-y-1.5 mt-auto">
        <div class="flex justify-between items-center text-xs">
          <span class="text-muted-foreground font-medium">Solde fin d'année</span>
          <span class="font-black tabular-nums text-sm" :class="balanceColor(endOfYearPersoBalance)">
            {{ formatNullableCurrency(endOfYearPersoBalance) }}
          </span>
        </div>
        <div class="flex justify-between items-center text-xs">
          <span class="text-muted-foreground font-medium">Revenus Imposables</span>
          <span class="font-bold text-emerald-400 tabular-nums">
            {{ formatCurrency(totalTaxableIncome) }}
          </span>
        </div>
        <div class="border-t border-emerald-500/20 pt-1.5 flex flex-col gap-1 text-sm">
          <div class="flex justify-between items-center">
            <span class="text-muted-foreground font-medium flex items-center gap-1">
              Impôt (IR) estimé
              <span v-if="isCalculating" class="animate-spin text-[10px]">⏳</span>
            </span>
            <span class="tabular-nums">
              <span v-if="isCalculating" class="inline-block w-12 h-3 bg-muted rounded animate-pulse"></span>
              <span v-else-if="estimationError" class="text-red-500 text-xs font-normal" :title="estimationError">Erreur</span>
              <span v-else>{{ formatNullableCurrency(estimatedTax) }}</span>
            </span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-muted-foreground font-medium">Impôt (IR) payé</span>
            <span class="tabular-nums text-indigo-400">{{ formatCurrency(persoTaxPaid) }}</span>
          </div>
          <div class="flex justify-between items-center mt-1 pt-1 border-t border-emerald-500/10">
            <span class="font-black text-foreground">Reste à payer</span>
            <span class="font-black tabular-nums text-indigo-400">
              <span v-if="isCalculating" class="inline-block w-12 h-3 bg-muted rounded animate-pulse"></span>
              <span v-else-if="estimationError" class="text-muted-foreground text-xs font-normal">--</span>
              <span v-else>{{ formatCurrency(Math.max(0, (estimatedTax ?? 0) - persoTaxPaid)) }}</span>
            </span>
          </div>
          <div class="flex justify-between items-center text-xs">
            <span class="text-muted-foreground font-medium cursor-help" :title="persoDebtCategoriesList">Autres dettes</span>
            <span class="tabular-nums text-indigo-400">
              <span v-if="isCalculating" class="inline-block w-12 h-3 bg-muted rounded animate-pulse"></span>
              <span v-else>{{ formatCurrency(persoOtherDebts) }}</span>
            </span>
          </div>
          <div class="flex justify-between items-center mt-2 pt-2 border-t-2 border-emerald-500/20">
            <span class="font-black text-foreground text-[10px] uppercase tracking-wider">Solde net</span>
            <span class="font-black tabular-nums text-sm" :class="balanceColor(netOfYearPersoBalance ?? 0)">
              <span v-if="isCalculating" class="inline-block w-16 h-4 bg-muted rounded animate-pulse"></span>
              <span v-else-if="estimationError" class="text-muted-foreground text-xs font-normal">--</span>
              <span v-else>{{ formatNullableCurrency(netOfYearPersoBalance) }}</span>
            </span>
          </div>
          <div class="flex justify-between items-center text-xs mt-1.5 pt-1.5 border-t border-emerald-500/10">
            <span class="text-muted-foreground font-medium">Bilan annuel</span>
            <span class="font-bold tabular-nums text-sm" :class="balanceColor(annualPersoBalanceDiff ?? 0)">
              <span v-if="isCalculating" class="inline-block w-12 h-3 bg-muted rounded animate-pulse"></span>
              <span v-else>{{ formatNullableBalance(annualPersoBalanceDiff) }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Professional account card -->
    <div class="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4 flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <span class="text-lg leading-none">💼</span>
        <span class="text-xs font-black uppercase tracking-wider text-indigo-400">Compte Pro</span>
      </div>
      <div class="space-y-1.5 mt-auto">
        <div class="flex justify-between items-center text-xs">
          <span class="text-muted-foreground font-medium">Solde fin d'année</span>
          <span class="font-black tabular-nums text-sm" :class="balanceColor(endOfYearProBalance)">
            {{ formatNullableCurrency(endOfYearProBalance) }}
          </span>
        </div>
        <div class="border-t border-indigo-500/20 pt-1.5 flex flex-col gap-1 text-sm">
          <div class="flex justify-between items-center">
            <span class="text-muted-foreground font-medium" title="Provision TNS (~45% de la rémunération nette)">URSSAF estimé</span>
            <span class="tabular-nums">{{ formatCurrency(proUrssafEstimation) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-muted-foreground font-medium">URSSAF payé</span>
            <span class="tabular-nums text-indigo-400">{{ formatCurrency(proUrssafPaid) }}</span>
          </div>
          <div class="flex justify-between items-center mt-1 pt-1 border-t border-indigo-500/10">
            <span class="font-black text-foreground">Reste à payer (URSSAF)</span>
            <span class="font-black tabular-nums text-indigo-400">
              {{ formatCurrency(Math.max(0, proUrssafEstimation - proUrssafPaid)) }}
            </span>
          </div>

          <div class="flex justify-between items-center mt-2 relative group">
            <span class="text-muted-foreground font-medium cursor-help border-b border-dashed border-muted-foreground/50 pb-0.5">IS estimé</span>
            <span class="tabular-nums">{{ formatCurrency(proIsEstimation) }}</span>

            <!-- Popover tooltip -->
            <div class="absolute bottom-full left-0 mb-1 hidden group-hover:flex flex-col gap-1.5 z-50 p-3 bg-card border border-border shadow-lg rounded-xl text-xs w-52 pointer-events-none">
              <div class="flex justify-between items-center">
                <span class="text-muted-foreground">Revenus (IS)</span>
                <span class="text-emerald-500">{{ formatCurrency(proIsIncome) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-muted-foreground">Charges (IS)</span>
                <span class="text-red-500">-{{ formatCurrency(proIsExpense) }}</span>
              </div>
              <div class="flex justify-between items-center mt-1 pt-1.5 border-t border-border/50">
                <span class="font-bold text-foreground">Bénéfice</span>
                <span class="font-bold text-foreground">{{ formatCurrency(proIsProfit) }}</span>
              </div>
            </div>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-muted-foreground font-medium">IS payé</span>
            <span class="tabular-nums text-indigo-400">{{ formatCurrency(proIsPaid) }}</span>
          </div>
          <div class="flex justify-between items-center mt-1 pt-1 border-t border-indigo-500/10">
            <span class="font-black text-foreground">Reste à payer (IS)</span>
            <span class="font-black tabular-nums text-indigo-400">
              {{ formatCurrency(Math.max(0, proIsEstimation - proIsPaid)) }}
            </span>
          </div>
          <div class="flex justify-between items-center text-xs">
            <span class="text-muted-foreground font-medium cursor-help" :title="proDebtCategoriesList">Autres dettes</span>
            <span class="tabular-nums text-indigo-400">{{ formatCurrency(proOtherDebts) }}</span>
          </div>
          <div class="flex justify-between items-center mt-2 pt-2 border-t-2 border-indigo-500/20">
            <span class="font-black text-foreground text-[10px] uppercase tracking-wider">Solde net</span>
            <span class="font-black tabular-nums text-sm" :class="balanceColor(netOfYearProBalance)">
              {{ formatNullableCurrency(netOfYearProBalance) }}
            </span>
          </div>
          <div class="flex justify-between items-center text-xs mt-1.5 pt-1.5 border-t border-indigo-500/10">
            <span class="text-muted-foreground font-medium">Bilan annuel</span>
            <span class="font-bold tabular-nums text-sm" :class="balanceColor(annualProBalanceDiff ?? 0)">
              {{ formatNullableBalance(annualProBalanceDiff) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { BudgetAccountData } from '../../../composables/useBudget'
import type { ResolvedBudgetData } from '../../../composables/useBudgetFormula'
import { useBudgetFormula } from '../../../composables/useBudgetFormula'
import { useUrssafApi, calculateCorporateTaxSync } from '../../../composables/useUrssafApi'
import { useBudget } from '../../../composables/useBudget'
import { watch } from 'vue'

// ── Props ─────────────────────────────────────────────────────────────────────

const props = defineProps<{
  year:                number
  personalData:        BudgetAccountData
  professionalData:    BudgetAccountData
  resolvedPersonal:    ResolvedBudgetData
  resolvedProfessional: ResolvedBudgetData
  previousPersonalCarry?: number | null
  previousProfessionalCarry?: number | null
  previousPersonalNet?: number | null
  previousProfessionalNet?: number | null
}>()

// ── Composables ───────────────────────────────────────────────────────────────

const { computeMonthlyTotals, computeAnnualTotals, computeOpeningBalances } = useBudgetFormula()
const { isEstimatingTax, estimatedTax, estimationError, estimateIncomeTax } = useUrssafApi()
const { taxStrategies } = useBudget()

// ── Computed ──────────────────────────────────────────────────────────────────

const proMonthlyTotals = computed(() => computeMonthlyTotals(props.professionalData.categories, props.resolvedProfessional))
const persoMonthlyTotals = computed(() => computeMonthlyTotals(props.personalData.categories, props.resolvedPersonal))

const proTotals = computed(() => computeAnnualTotals(proMonthlyTotals.value))
const persoTotals = computed(() => computeAnnualTotals(persoMonthlyTotals.value))

const persoOpeningBalances = computed(() => computeOpeningBalances(props.personalData.openingBalances, persoMonthlyTotals.value, props.previousPersonalCarry))
const endOfYearPersoBalance = computed(() => {
  const opening = persoOpeningBalances.value[12]?.amount
  if (opening === null || opening === undefined) return null
  return opening + (persoMonthlyTotals.value[12]?.balance ?? 0)
})

const proOpeningBalances = computed(() => computeOpeningBalances(props.professionalData.openingBalances, proMonthlyTotals.value, props.previousProfessionalCarry))
const endOfYearProBalance = computed(() => {
  const opening = proOpeningBalances.value[12]?.amount
  if (opening === null || opening === undefined) return null
  return opening + (proMonthlyTotals.value[12]?.balance ?? 0)
})

const annualProBalanceDiff = computed(() => {
  if (netOfYearProBalance.value === null) return null
  const prevNet = props.previousProfessionalNet !== null && props.previousProfessionalNet !== undefined
    ? props.previousProfessionalNet
    : (proOpeningBalances.value[1]?.amount ?? 0)
  return netOfYearProBalance.value - prevNet
})

const annualPersoBalanceDiff = computed(() => {
  if (netOfYearPersoBalance.value === null) return null
  const prevNet = props.previousPersonalNet !== null && props.previousPersonalNet !== undefined
    ? props.previousPersonalNet
    : (persoOpeningBalances.value[1]?.amount ?? 0)
  return netOfYearPersoBalance.value - prevNet
})

const proOtherDebts = computed(() => {
  let sum = 0
  for (const cat of props.professionalData.categories) {
    if (cat.type === 'parameter' && cat.isDebt) {
      sum += (props.resolvedProfessional[cat.id]?.[12] ?? 0)
    }
  }
  return sum
})

const persoOtherDebts = computed(() => {
  let sum = 0
  for (const cat of props.personalData.categories) {
    if (cat.type === 'parameter' && cat.isDebt) {
      sum += (props.resolvedPersonal[cat.id]?.[12] ?? 0)
    }
  }
  return sum
})

const persoDebtCategoriesList = computed(() => {
  const names = props.personalData.categories
    .filter(cat => cat.type === 'parameter' && cat.isDebt)
    .map(cat => cat.name)
  return names.length > 0
    ? `Catégories prises en compte : ${names.join(', ')}`
    : 'Aucune catégorie de dette'
})

const proDebtCategoriesList = computed(() => {
  const names = props.professionalData.categories
    .filter(cat => cat.type === 'parameter' && cat.isDebt)
    .map(cat => cat.name)
  return names.length > 0
    ? `Catégories prises en compte : ${names.join(', ')}`
    : 'Aucune catégorie de dette'
})

const netOfYearProBalance = computed(() => {
  if (endOfYearProBalance.value === null) return null
  const restToPayUrssaf = Math.max(0, proUrssafEstimation.value - proUrssafPaid.value)
  const restToPayIs = Math.max(0, proIsEstimation.value - proIsPaid.value)
  return endOfYearProBalance.value - restToPayUrssaf - restToPayIs - proOtherDebts.value
})

const netOfYearPersoBalance = computed(() => {
  if (endOfYearPersoBalance.value === null || estimatedTax.value === null) return null
  if (estimationError.value) return null
  const restToPay = Math.max(0, estimatedTax.value - persoTaxPaid.value)
  return endOfYearPersoBalance.value - restToPay - persoOtherDebts.value
})

const totalTaxableIncome = computed(() => {
  let total = 0
  for (const cat of props.personalData.categories) {
    if (cat.type === 'income' && cat.isTaxable) {
      for (let m = 1; m <= 12; m++) {
        total += (props.resolvedPersonal[cat.id]?.[m] ?? 0)
      }
    }
  }
  return total
})

const persoTaxPaid = computed(() => {
  let totalPaid = 0
  for (const cat of props.personalData.categories) {
    if (cat.type === 'expense' && cat.isTaxPayment) {
      for (let m = 1; m <= 12; m++) {
        totalPaid += (props.resolvedPersonal[cat.id]?.[m] ?? 0)
      }
    }
  }
  return totalPaid
})

const proUrssafEstimation = computed(() => {
  let totalTns = 0
  for (const cat of props.professionalData.categories) {
    if (cat.type === 'expense' && cat.isTnsRemuneration) {
      for (let m = 1; m <= 12; m++) {
        totalTns += (props.resolvedProfessional[cat.id]?.[m] ?? 0)
      }
    }
  }
  const strategy = taxStrategies.value[props.year]
  const urssafRate = strategy?.urssafRate ?? 0.45
  return totalTns * urssafRate
})

const proUrssafPaid = computed(() => {
  let totalPaid = 0
  for (const cat of props.professionalData.categories) {
    if (cat.type === 'expense' && cat.isUrssafPayment) {
      for (let m = 1; m <= 12; m++) {
        totalPaid += (props.resolvedProfessional[cat.id]?.[m] ?? 0)
      }
    }
  }
  return totalPaid
})

const proIsIncome = computed(() => {
  let income = 0
  for (const cat of props.professionalData.categories) {
    if (cat.corporateTaxDirection === 'income') {
      for (let m = 1; m <= 12; m++) {
        income += (props.resolvedProfessional[cat.id]?.[m] ?? 0)
      }
    }
  }
  return income
})

const proIsExpense = computed(() => {
  let expense = 0
  for (const cat of props.professionalData.categories) {
    if (cat.corporateTaxDirection === 'expense') {
      for (let m = 1; m <= 12; m++) {
        expense += (props.resolvedProfessional[cat.id]?.[m] ?? 0)
      }
    }
  }
  return expense
})

const proIsProfit = computed(() => proIsIncome.value - proIsExpense.value)

const proIsEstimation = computed(() => {
  return calculateCorporateTaxSync(proIsProfit.value, props.year, taxStrategies.value)
})

const proIsPaid = computed(() => {
  let totalPaid = 0
  for (const cat of props.professionalData.categories) {
    if (cat.type === 'expense' && cat.isCorporateTaxPayment) {
      for (let m = 1; m <= 12; m++) {
        totalPaid += (props.resolvedProfessional[cat.id]?.[m] ?? 0)
      }
    }
  }
  return totalPaid
})

// ── Watchers ──────────────────────────────────────────────────────────────────

const isCalculating = ref(false)

let taxTimeout: ReturnType<typeof setTimeout> | null = null
watch([totalTaxableIncome, () => props.year], (newValues) => {
  const [newTotal, activeYear] = newValues
  isCalculating.value = true
  estimatedTax.value = null
  
  if (taxTimeout) clearTimeout(taxTimeout)
  taxTimeout = setTimeout(async () => {
    try {
      await estimateIncomeTax(newTotal, activeYear)
    } finally {
      isCalculating.value = false
    }
  }, 800)
}, { immediate: true })

const globalTotals = computed(() => {
  const proBal = annualProBalanceDiff.value
  const persoBal = annualPersoBalanceDiff.value
  
  return {
    income:  proTotals.value.income  + persoTotals.value.income,
    expense: proTotals.value.expense + persoTotals.value.expense,
    balance: (proBal === null || persoBal === null) ? null : (proBal + persoBal),
  }
})

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatBalance(amount: number): string {
  return (amount > 0 ? '+' : '') + formatCurrency(amount)
}

function balanceColor(amount: number): string {
  if (amount > 0) return 'text-emerald-500 dark:text-emerald-400'
  if (amount < 0) return 'text-red-500 dark:text-red-400'
  return 'text-muted-foreground'
}

function formatNullableCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '—'
  return formatCurrency(amount)
}

function formatNullableBalance(amount: number | null): string {
  if (amount === null || amount === undefined) return '—'
  return formatBalance(amount)
}
</script>
